/**
 * Core / Mint: Core
 * ------------------------------------------------------------------------------
 * - This single instance is renderless, and is used to set Pinia states.
 * - By rendering a single instance, global states (like window resize) can
 *   be performed and set once, then synchronised across all Vue instances.
 *
 * @namespace MintCore
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'

import CoreMethods from '~/core/mint/methods/core-methods'
import CoreComputed from '~/core/mint/methods/core-computed'

/**
 * DOM selectors.
 */
const selectors = {
  container: '[data-mint-core]',
}

/**
 * Create a new Mint Core object.
 */
export default () => {

  /**
   * Initialise component.
   */
  function init() {
    const app = createApp({
      name: 'Core',
      delimiters: ['${', '}'],
      computed: {
        ...CoreComputed(),
      },
      mounted() {

        /**
         * Methods within core-methods.js
         */
        this.setEventListeners()
        this.setEventBusListeners()
        this.initWishlist()

        /**
         * Pinia methods:
         */
        this.queryLocale()

        this.handleCartUpdate({
          event: 'cart:initialise',
          preventTracking: true,
        })

        if (mint.request.pageType === 'product') {
          const urlParams = new URLSearchParams(location.search)
          const initialVariantId = urlParams.get('variant') || false
          const flexi = urlParams.get('flexi') || false

          this.queryProduct({
            handle: mint.product.handle,
            variantId: initialVariantId,
            flexi,
          })
        }

        /**
         * Execute Pinia functions on mount.
         * - Any method call here should also be added:
         * - mint-components.js
         * - mint-core.js
         * - mint-section.js
         * - Ensures that Pinia state is shared between all
         *   Vue instances.
         */
        this.setFeatureFlags()
        this.handleResize()
      },
      methods: CoreMethods(),
    })

    /**
     * Create core app.
     */
    app.use(createPinia())

    app.mount(selectors.container)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}